// hooks/useUpgradePrompt.ts
import { useState, useCallback } from "react";
import { PlanRestrictionAPIError } from "@/lib/enhancedAuthenticatedFetch";
import {
  PlanRestrictionError,
  getFeatureDisplayName,
  parsePlanRestrictionResponse,
  planRestrictionFromHttpError,
  isUsageLimitExceeded,
} from "@/lib/planRestrictionHandler";

export type PromptType = "upgrade" | "usage-limit";

export interface UpgradePromptState {
  isOpen: boolean;
  type: PromptType;
  feature: string;
  currentPlan: string;
  error?: PlanRestrictionError;
}

export function useUpgradePrompt() {
  const [promptState, setPromptState] = useState<UpgradePromptState>({
    isOpen: false,
    type: "upgrade",
    feature: "",
    currentPlan: "free",
  });

  const handlePlanRestrictionError = useCallback(
    (error: PlanRestrictionAPIError | PlanRestrictionError) => {
      let restriction: PlanRestrictionError;

      if (error instanceof PlanRestrictionAPIError) {
        restriction = error.planRestriction;
      } else {
        restriction = error;
      }

      const feature = getFeatureDisplayName(restriction.feature);
      const type: PromptType = isUsageLimitExceeded(restriction)
        ? "usage-limit"
        : "upgrade";

      setPromptState({
        isOpen: true,
        type,
        feature,
        currentPlan: promptState.currentPlan,
        error: restriction,
      });
    },
    [promptState.currentPlan],
  );

  /**
   * Check if a Response is a plan restriction (403 or 429) and show prompt
   * @returns true if it was a plan restriction error
   */
  const checkResponseForPlanRestriction = useCallback(
    async (response: Response): Promise<boolean> => {
      if (response.status !== 403 && response.status !== 429) {
        return false;
      }

      const restriction = await parsePlanRestrictionResponse(response);
      if (restriction) {
        handlePlanRestrictionError(restriction);
        return true;
      }
      return false;
    },
    [handlePlanRestrictionError],
  );

  /**
   * Check if an error is a plan restriction and show prompt.
   * Recognizes PlanRestrictionAPIError (enhancedAuthenticatedFetch)
   * and HttpError (safeFetch) carrying a plan-restriction body.
   * @returns true if it was a plan restriction error
   */
  const checkForPlanRestriction = useCallback(
    (error: unknown): boolean => {
      if (error instanceof PlanRestrictionAPIError) {
        handlePlanRestrictionError(error);
        return true;
      }
      const restriction = planRestrictionFromHttpError(error);
      if (restriction) {
        handlePlanRestrictionError(restriction);
        return true;
      }
      return false;
    },
    [handlePlanRestrictionError],
  );

  const closeUpgradePrompt = useCallback(() => {
    setPromptState((prev) => ({
      ...prev,
      isOpen: false,
      feature: "",
      error: undefined,
    }));
  }, []);

  const setCurrentPlan = useCallback((plan: string) => {
    setPromptState((prev) => ({ ...prev, currentPlan: plan }));
  }, []);

  return {
    // State
    isUpgradePromptOpen: promptState.isOpen,
    promptType: promptState.type,
    restrictedFeature: promptState.feature,
    currentPlan: promptState.currentPlan,
    restrictionError: promptState.error,

    // Actions
    checkForPlanRestriction,
    checkResponseForPlanRestriction,
    handlePlanRestrictionError,
    closeUpgradePrompt,
    setCurrentPlan,
  };
}
